import { Component, type ErrorInfo, type ReactNode } from 'react'
import { safeSessionGet, safeSessionSet } from '../utils/safeStorage'

/**
 * Last line of defence against a blank page.
 *
 * Without a boundary at the root, any error thrown during render — or inside a
 * useEffect — unmounts the entire React tree and leaves the visitor staring at
 * an empty white screen with no way to recover. That failure mode shows up far
 * more on phones: flaky networks drop lazy-loaded route chunks, in-app browsers
 * (WhatsApp, Instagram, Facebook) serve stale HTML that points at chunk hashes
 * which no longer exist after a deploy, and low-memory devices fail WebGL.
 *
 * The most common of those, a failed chunk fetch after a new deployment, is
 * fully recoverable: reloading pulls fresh HTML with correct chunk URLs. So we
 * do that automatically, with a counter to avoid an endless reload loop when the
 * problem is something else.
 *
 * NOTE: the reload counter goes through safeStorage. An earlier version of this
 * component read sessionStorage directly, which throws in exactly the restricted
 * contexts we are trying to protect (iOS private mode, in-app webviews) — the
 * boundary itself became the crash, which is why it was previously deleted.
 */

const RELOAD_COUNT_KEY = 'bdg-chunk-reload-count'
const MAX_AUTO_RELOADS = 2

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  isRecovering: boolean
}

function isChunkLoadError(error: Error): boolean {
  const message = error.message ?? ''
  return (
    error.name === 'ChunkLoadError' ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('dynamically imported module') ||
    // Safari's wording when a module script fails to load.
    message.includes('Importing a module script failed')
  )
}

class GlobalErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, isRecovering: false }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[app] uncaught error:', error, errorInfo.componentStack)

    if (!isChunkLoadError(error)) return

    // A stale document referencing deleted chunks — reload to get fresh HTML.
    const attempts = Number(safeSessionGet(RELOAD_COUNT_KEY) ?? '0')
    if (attempts >= MAX_AUTO_RELOADS) return

    safeSessionSet(RELOAD_COUNT_KEY, String(attempts + 1))
    this.setState({ isRecovering: true })
    // Brief delay so we never hammer the server in a tight loop.
    window.setTimeout(() => window.location.reload(), 600)
  }

  private handleRetry = () => {
    safeSessionSet(RELOAD_COUNT_KEY, '0')
    window.location.reload()
  }

  render() {
    if (!this.state.hasError) return this.props.children

    if (this.state.isRecovering) {
      return (
        <div style={styles.wrap} role="status" aria-live="polite">
          <p style={styles.title}>Updating to the latest version…</p>
          <p style={styles.body}>This will only take a moment.</p>
        </div>
      )
    }

    return (
      <div style={styles.wrap} role="alert">
        <p style={styles.title}>Something went wrong</p>
        <p style={styles.body}>
          Sorry about that. Reloading the page usually sorts it out.
        </p>
        <button type="button" onClick={this.handleRetry} style={styles.button}>
          Reload page
        </button>
      </div>
    )
  }
}

// Inline styles so the fallback still renders if the stylesheet is what failed.
const styles: Record<string, React.CSSProperties> = {
  wrap: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: '24px',
    textAlign: 'center',
    fontFamily: "'Manrope', system-ui, -apple-system, sans-serif",
    color: '#0f2b1f',
    background: '#ffffff',
  },
  title: { margin: 0, fontSize: 18, fontWeight: 700 },
  body: { margin: 0, fontSize: 14, color: 'rgba(15,43,31,0.65)', maxWidth: 320 },
  button: {
    marginTop: 8,
    padding: '10px 22px',
    border: 'none',
    borderRadius: 24,
    background: '#2ea872',
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
}

export default GlobalErrorBoundary
